import { parse } from '@lib/parse';
import { separate } from '@packages/strings';
import { LocationState } from 'history';
import * as qs from 'query-string';
import { StaticContext, useLocation, useParams as useParams$ } from 'react-router';
import $useRouter from 'use-react-router';

const useReactRouter = $useRouter;

// Alias to use-react-router package, for a better autocomplete
export { useReactRouter };

export function useRouter<
    P extends Record<string, any> = Record<string, any>,
    C extends StaticContext = StaticContext,
    S = LocationState,
>() {
    const router = $useRouter<P, C, S>();

    return {
        ...router,
        router,
    };
}

/**
 * Router params mapper;
 * @example
 * // /my/route/:id/view/:step
 * // /my/route/1/view/basics
 * const params = useRouterParams(['id', 'set']);
 * console.log(params) // { id: 1, step: 'basics' }
 *
 * @param params$
 * @returns {*}
 */
export const useRouterParams = <T = { [key: string]: any }>(params$: any = []): T => {
    const location = useLocation();
    const search = qs.parse(location.search);

    const params = {
        ...(useParams$() ?? {}),
        ...(search ?? {}),
    };

    if (typeof params$ === 'string') {
        params$ = separate(params$, ',');
    }

    if (!params$ || params$.length === 0) {
        // If there's no args passed, pass all params to be parsed.
        params$ = Object.keys(params);
    }

    if (typeof params$ === 'object') {
        if (Array.isArray(params$)) {
            params$ = params$.map((v) => [v, v]);
        } else {
            params$ = Object.entries(params$);
        }
    }

    return params$.reduce(
        (obj, [key, value]) => ({
            ...obj,
            [key]: parse((params || {})[value]),
        }),
        {},
    );
};

export const useParams = useRouterParams;
